import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { getValue } from "@mobility-item-page/utilities";
import { Attention } from "@warp-ds/elements/react/attention";
import { Button } from "@warp-ds/react";
import { useRef, useState } from "react";
import { Icon } from "./Icon.tsx";

type Props = {
  field: string;
  value?: string | IAdViewManaged | number | null | boolean;
  link?: {
    href?: string;
    text: string;
  };
  tooltip?: string;
  suppressHydrationWarning?: boolean;
  sup?: string;
};

const FieldValuePair = ({ field: rawField, value: rawValue, link, tooltip, suppressHydrationWarning, sup }: Props) => {
  const { i18n } = useLingui();

  const yesText = i18n.t({
    id: "itemPageLayout.keyInfo.yes",
    message: "Yes",
    comment: "Field for 'yes' in ad specification",
  });
  const noText = i18n.t({
    id: "itemPageLayout.keyInfo.no",
    message: "No",
    comment: "Field for 'no' in ad specification",
  });

  let value = typeof rawValue === "object" ? getValue(rawValue) : rawValue;

  if (typeof rawValue === "boolean") {
    value = rawValue ? yesText : noText;
  }
  if ((value == null || value === "") && !link) {
    return null;
  }

  const words = rawField.split(" ");
  const field = tooltip ? (
    <>
      {words.slice(0, -1).join(" ")}
      {words.length > 1 && " "}
      <span className="whitespace-nowrap">
        {words.at(-1)}
        <Tooltip tooltip={tooltip} field={rawField} />
      </span>
    </>
  ) : (
    rawField
  );

  const getContent = () => {
    if (link?.href && value) {
      return (
        <>
          {value}{" "}
          <a href={link.href} target="_blank" rel="noopener">
            ({link.text})
          </a>
        </>
      );
    }
    if (!link && value != null) {
      return value;
    }
    if (link && !value) {
      return (
        <a className="t4" href={link.href} target="_blank" rel="noopener">
          {link.text}
        </a>
      );
    }
    return null;
  };

  return (
    <div className="pb-16 md:pb-20" style={{ breakInside: "avoid-column" }}>
      <dt className="capitalize&::first-letter m-0 s-text-subtle">{field}</dt>
      <dd className="t4 m-0" suppressHydrationWarning={suppressHydrationWarning}>
        {getContent()}
        {sup && <sup>{sup}</sup>}
      </dd>
    </div>
  );
};

const Tooltip = ({ tooltip, field }: { tooltip: string; field: string }) => {
  const targetEl = useRef(null);
  const [showTooltip, setShowtoolTip] = useState(false);
  const tooltipId = `tooltip-${field
    .replace(/[()[\]{}]/g, "")
    .replace(/\s/g, "-")
    .toLowerCase()}`;

  return (
    <span className="inline-flex ml-4">
      <Attention
        tooltip
        flip
        fallbackPlacements={["left", "top", "bottom"]}
        placement="right"
        show={showTooltip}
        className="sm:max-w-screen-sm whitespace-normal"
      >
        <Button
          slot="target"
          link
          className="align-sub"
          ref={targetEl}
          aria-describedby={tooltipId}
          aria-expanded={showTooltip}
          onMouseEnter={() => setShowtoolTip(true)}
          onMouseLeave={() => setShowtoolTip(false)}
          onFocus={() => setShowtoolTip(true)}
          onBlur={() => setShowtoolTip(false)}
        >
          <Icon name="Info" />
        </Button>

        <p slot="message" id={tooltipId}>
          {tooltip}
        </p>
      </Attention>
    </span>
  );
};

export default FieldValuePair;
